import { type Dispatch, Fragment, type SetStateAction, type SyntheticEvent, useCallback, useState } from "react";

import {
  Aggregator,
  AnalyticsDataSource,
  Metadata,
  type Metric,
  Positions,
  type ReportConfig,
} from "@doitintl/cmp-models";
import { Box } from "@mui/material";
import findIndex from "lodash/findIndex";
import sortBy from "lodash/sortBy";

import { reportText, tooltipText } from "../../../../assets/texts";
import { DraggableContainer } from "../../../../Components/DragAndDrop/DraggableContainer";
import useUpdateEffect from "../../../../Components/hooks/useUpdateEffect";
import MetricSelect from "../../../../Components/Selects/CloudAnalytics/MetricSelect";
import { useIsFeatureEntitled } from "../../../../Context/TierProvider";
import { type MetadataOption, type MetricWSnap } from "../../../../types";
import { FilterModeOption } from "../../allocations/types";
import { type KeyTypeValues } from "../../api";
import { useMetricSplitsContext, useReportConfig, useReportDimensionsContext } from "../../Context";
import { type ColKeySort } from "../../ReportData";
import {
  type ErrorCode,
  getTypeString,
  limitResultsIds,
  type ReportAddDimension,
  shouldShowTypeLabel,
} from "../../utilities";
import { type PopoverAnchorEl } from "../types";
import AdvancedOptions from "./AdvancedOptions";
import AggregatorCount from "./AggregatorCount";
import { DataSourceSection } from "./DataSourceSection";
import DimensionItemMenu from "./DimensionItemMenu";
import Dimensions from "./Dimensions";
import FilterResults from "./FilterResults";
import ForecastSettings from "./ForecastSettings/ForecastSettings";
import GroupBy from "./GroupBy";
import LimitResults from "./LimitResults";
import ReportItem from "./ReportItem";
import TimeSelection from "./TimeSelection";

const mouseSensorOptions = {
  activationConstraint: {
    delay: 100,
    tolerance: 50,
  },
};

const disabledDropItems = [
  "datetime:year",
  "datetime:month",
  "datetime:day",
  "datetime:hour",
  "datetime:week",
  "datetime:quarter",
  "datetime:week_day",
];

type Props = {
  handleMetricSelect: (metricVal: Metric, value: MetricWSnap | string) => void;
  handleAggregatorSelect: (aggregator: Aggregator) => void;
  isCSP: boolean;
  queryRunning: boolean;
  handleResetLimits: () => void;
  popoverAnchorEl: any;
  setPopoverAnchorEl: (anchorEl: PopoverAnchorEl | null) => void;
  handleFilterOperation: () => void;
  handleChangeLabels: (values: KeyTypeValues[], position: Positions) => void;
  handleAddDimension: ReportAddDimension;
  handleDelete: (option: MetadataOption[]) => void;
  touched: Set<any>;
  setColKeySort: Dispatch<SetStateAction<ColKeySort | undefined>>;
  colSortDisabled: boolean;
  resetReport: (newConfig: ReportConfig) => void;
  handleReportError: (code: ErrorCode) => void;
};

const DraggableGroupWrapper = ({
  handleMetricSelect,
  handleAggregatorSelect,
  isCSP,
  queryRunning,
  handleResetLimits,
  handleDelete,
  handleAddDimension,
  handleChangeLabels,
  handleFilterOperation,
  popoverAnchorEl,
  setPopoverAnchorEl,
  touched,
  setColKeySort,
  colSortDisabled,
  resetReport,
  handleReportError,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | Element>(null);
  const [selectedSplitItem, setSelectedSplitItem] = useState<MetadataOption | undefined>();
  const isEntitledAnalyticsForecasts = useIsFeatureEntitled("analytics:forecasts");

  const { metricSplits, openSplitModal, onIncludeOrigin } = useMetricSplitsContext();
  const { dimensions = [], handleDraggableItemsUpdate, handleReportFilterChange } = useReportDimensionsContext();
  const {
    reportConfig: {
      metric,
      calculatedMetric,
      extendedMetric: selectedExtendedMetric,
      extendedMetrics,
      datahubMetrics,
      aggregator,
      rows,
      cols,
      filters,
      dataSource,
    },
  } = useReportConfig();

  const disabledDrop = useCallback(
    (id: string, section: string) => section === Positions.UNUSED && disabledDropItems.includes(id),
    []
  );

  const openSplitMenu = (event: SyntheticEvent, item?: MetadataOption) => {
    setAnchorEl(event.currentTarget);
    setSelectedSplitItem(item);
  };

  const handleCloseDimensionItemMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const metricSplitModalClicked = useCallback(() => {
    if (selectedSplitItem && openSplitModal) {
      openSplitModal(selectedSplitItem);
      handleCloseDimensionItemMenu();
    }
  }, [selectedSplitItem, openSplitModal, handleCloseDimensionItemMenu]);

  const handleOpenFilterDialog = useCallback(
    (event: SyntheticEvent) => {
      if (selectedSplitItem && selectedSplitItem.data.type !== Metadata.DATETIME) {
        handleAddDimension([selectedSplitItem])(event);
        handleCloseDimensionItemMenu();
      }
    },
    [selectedSplitItem, handleAddDimension, handleCloseDimensionItemMenu]
  );

  const handleClearFilters = useCallback(
    (selectedItem: MetadataOption) => {
      // Clear filters by setting both _filter and _regexp to null
      handleReportFilterChange(selectedItem.id, FilterModeOption.IS, null, false);
    },
    [handleReportFilterChange]
  );

  const calculateDraggableItems = useCallback(
    () => ({
      [Positions.ROW]: dimensions
        .filter((option) => option._visible && option._position === Positions.ROW)
        .map((item) => item.id),
      [Positions.COL]: dimensions
        .filter((option) => option._visible && option._position === Positions.COL)
        .map((item) => item.id),
      [Positions.UNUSED]: dimensions
        .filter(
          (option) => option._visible && option._position === Positions.UNUSED && !limitResultsIds.includes(option.id)
        )
        .map((item) => item.id),
    }),
    [dimensions]
  );

  const initCalculateDraggableItems = useCallback(() => {
    const items = calculateDraggableItems();
    items[Positions.ROW] = sortBy(items[Positions.ROW], (i) => findIndex(rows, (r) => i === r));
    items[Positions.COL] = sortBy(items[Positions.COL], (i) => findIndex(cols, (c) => i === c));
    items[Positions.UNUSED] = sortBy(items[Positions.UNUSED], (i) => findIndex(filters, (f) => i === f.id));
    return items;
  }, [rows, cols, filters, calculateDraggableItems]);

  const [draggableItems, setDraggableItems] = useState<Record<string, string[]>>(initCalculateDraggableItems());

  useUpdateEffect(() => {
    setDraggableItems(calculateDraggableItems());
  }, [calculateDraggableItems]);

  const updateDraggableItems = useCallback(
    (items: Record<string, string[]>) => {
      handleDraggableItemsUpdate(items);
      setDraggableItems(items);
    },
    [handleDraggableItemsUpdate]
  );

  const getItemTooltip = useCallback(
    (itemDisabled: boolean) => (itemDisabled ? reportText.DISABLED_CHIP_CUSTOM_METRIC : ""),
    []
  );

  const renderDraggingItem = useCallback(
    (id: string) => (
      <ReportItem
        item={dimensions.find((option) => option.id === id)}
        id={id}
        onItemRemove={handleDelete}
        handleAddDimension={handleAddDimension}
        isDraggable={true}
        grabbing
      />
    ),
    [dimensions, handleAddDimension, handleDelete]
  );

  const renderDimensionItemMenu = useCallback(
    () => (
      <DimensionItemMenu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseDimensionItemMenu}
        metricSplitModalClicked={metricSplitModalClicked}
        selectedDimension={selectedSplitItem}
        onOpenFilterDialog={handleOpenFilterDialog}
        onClearFilters={handleClearFilters}
        isInFilterResults={selectedSplitItem?._position === Positions.UNUSED}
      />
    ),
    [
      anchorEl,
      metricSplitModalClicked,
      selectedSplitItem,
      handleOpenFilterDialog,
      handleCloseDimensionItemMenu,
      handleClearFilters,
    ]
  );

  const renderReportItems = useCallback(
    (sectionId: string, items: string[], disabled: boolean, isHidden: boolean) => (
      <Box sx={{ display: isHidden ? "none" : "block" }}>
        {items.map((id) => {
          const item = dimensions.find((option) => option.id === id);
          const splits = Object.values(metricSplits ?? []).filter((split) => split.id === item?.id);
          const showTypeLabel = shouldShowTypeLabel(item);
          return (
            <ReportItem
              key={id}
              item={item}
              id={id}
              onItemRemove={handleDelete}
              handleAddDimension={handleAddDimension}
              isDraggable={true}
              disabled={disabled}
              tooltip={getItemTooltip(!!item?._disabled)}
              openSplitMenu={openSplitMenu}
              splitsLen={splits.length}
              includeOrigin={splits.length > 0 && splits[0].includeOrigin}
              onIncludeOrigin={onIncludeOrigin}
              typeLabel={showTypeLabel && item ? getTypeString(item, false, false, true) : undefined}
            />
          );
        })}
        {renderDimensionItemMenu()}
      </Box>
    ),
    [
      dimensions,
      handleAddDimension,
      handleDelete,
      getItemTooltip,
      metricSplits,
      renderDimensionItemMenu,
      onIncludeOrigin,
    ]
  );

  const renderDraggableContent = useCallback(
    (sectionId: string, disabled: boolean, isHidden: boolean) => (items: string[]) =>
      renderReportItems(sectionId, items, disabled, isHidden),
    [renderReportItems]
  );

  const popoverForFilter = (anchorEl: PopoverAnchorEl | null) => {
    setPopoverAnchorEl(anchorEl);
    handleFilterOperation();
  };

  const dataSourceKubernetesUtil = dataSource === AnalyticsDataSource.KUBERNETES_UTILIZATION;

  return (
    <DraggableContainer
      values={draggableItems}
      onItemsOrderChanged={updateDraggableItems}
      renderDraggingItem={renderDraggingItem}
      mouseSensorOptions={mouseSensorOptions}
      disabledDrop={disabledDrop}
    >
      <DataSourceSection resetReport={resetReport} />

      <MetricSelect
        extendedMetrics={extendedMetrics}
        datahubMetrics={datahubMetrics}
        handleMetricSelect={handleMetricSelect}
        isCSP={isCSP}
        selectedCalculatedMetric={calculatedMetric}
        selectedExtendedMetric={selectedExtendedMetric}
        selectedMetric={metric}
        handleResetLimits={handleResetLimits}
        dataSource={dataSource}
        disabled={aggregator === Aggregator.COUNT}
        disabledTooltip={tooltipText.METRIC_FILTER_DISABLED}
        sx={{ mb: 4 }}
      />

      <TimeSelection queryRunning={queryRunning} />

      <FilterResults
        handleAddDimension={handleAddDimension}
        handleChangeLabels={handleChangeLabels}
        popoverAnchorEl={popoverAnchorEl}
        setPopoverAnchorEl={popoverForFilter}
        numOfItems={draggableItems[Positions.UNUSED].length}
        renderDraggableContent={renderDraggableContent}
      />

      <GroupBy
        handleAddDimension={handleAddDimension}
        handleChangeLabels={handleChangeLabels}
        popoverAnchorEl={popoverAnchorEl}
        setPopoverAnchorEl={popoverForFilter}
        touched={touched}
        setColKeySort={setColKeySort}
        numOfItems={draggableItems[Positions.ROW].length}
        renderDraggableContent={renderDraggableContent}
        handleReportError={handleReportError}
      />

      <Fragment>{isEntitledAnalyticsForecasts && !dataSourceKubernetesUtil && <ForecastSettings />}</Fragment>

      <AggregatorCount
        handleChangeLabels={handleChangeLabels}
        setPopoverAnchorEl={setPopoverAnchorEl}
        popoverAnchorEl={popoverAnchorEl}
        handleDelete={handleDelete}
        handleAddDimension={handleAddDimension}
        getItemTooltip={getItemTooltip}
      />

      <LimitResults
        handleAddDimension={handleAddDimension}
        handleDelete={handleDelete}
        getItemTooltip={getItemTooltip}
      />

      <Fragment>
        {!dataSourceKubernetesUtil && (
          <AdvancedOptions handleAggregatorSelect={handleAggregatorSelect} queryRunning={queryRunning} />
        )}
      </Fragment>

      <Dimensions
        handleAddDimension={handleAddDimension}
        handleChangeLabels={handleChangeLabels}
        popoverAnchorEl={popoverAnchorEl}
        setPopoverAnchorEl={setPopoverAnchorEl}
        colSortDisabled={colSortDisabled}
        numOfItems={draggableItems[Positions.COL].length}
        renderDraggableContent={renderDraggableContent}
      />
    </DraggableContainer>
  );
};

export default DraggableGroupWrapper;
